/* eslint-disable valid-jsdoc */
/* @flow */

import { DateTime } from 'luxon';
import Video from 'twilio-video';
import { getEventPlugins } from '@e180/domain/lib/event/util';
import * as Sentry from '@sentry/browser';

import {
  getBraindateId,
  getBraindateStartTime,
  getBraindateEndTime,
} from '@e180/domain/lib/braindate/util';

import { isErrorReportingEnabled } from 'src/shared/app/base/selector/appEnvSelectors';
import {
  videoCallPlugin,
  videoCallPluginProviderTwilio,
} from 'src/shared/app/feature/settings/featureSettings';
import { takeOverMinutesToBraindate } from 'plugin/virtual-braindate/setting/virtualBraindateSettings';
import { createDateTimeFromISO } from 'src/shared/app/new-calendar/util/dateUtils';
import {
  notificationInfoLevel,
  notificationTopType,
  notificationTakeOverType,
} from 'src/shared/app/notification/constant/notificationConstants';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

const debug = require('debug')('virtualBraindateUtils');

export function silentError(e) {
  if (isErrorReportingEnabled) {
    Sentry.captureException(e);
  }
  console.error(e);
}

export function showVirtualBraindateTakeOver(braindate: Braindate): boolean {
  const startTime = getBraindateStartTime(braindate);
  const startDateTime = DateTime.fromISO(startTime);
  const takeOverDateTime = startDateTime.minus({
    minutes: takeOverMinutesToBraindate,
  });

  return takeOverDateTime.diffNow().milliseconds < 0;
}

export function buildVirtualBraindateNotification(
  braindate: Braindate,
): NotificationType {
  const showTakeOver = showVirtualBraindateTakeOver(braindate);

  return {
    id: `virtual-braindate-reminder-${getBraindateId(braindate)}`,
    level: notificationInfoLevel,
    type: showTakeOver ? notificationTakeOverType : notificationTopType,
    componentClass: showTakeOver
      ? 'VirtualBraindateReminderTakeOver'
      : 'VirtualBraindateReminderNotification',
    data: braindate,
    exclude: showTakeOver ? ['braindateRoute'] : ['braindateRoute:id'],
  };
}

export function buildVirtualBraindateEarlyReminder(
  braindate: Braindate,
): NotificationType {
  return {
    id: `virtual-braindate-early-reminder-${getBraindateId(braindate)}`,
    level: notificationInfoLevel,
    type: notificationTopType,
    componentClass: 'VirtualBraindateEarlyReminder',
    data: braindate,
    exclude: ['braindateRoute'],
  };
}

export function getSecondsBeforeBraindateEndTime(braindate: Braindate): number {
  // $FlowIssue
  const endTime = createDateTimeFromISO(getBraindateEndTime(braindate));
  const { milliseconds } = endTime.diffNow();
  const seconds = milliseconds / 1000;

  return Math.round(seconds);
}

/**
 * Get the list of available media devices of the given kind.
 * @param {Array<MediaDeviceInfo>} deviceInfos
 * @param {string} kind - One of 'audioinput', 'audiooutput', 'videoinput'
 * @returns {Array<MediaDeviceInfo>} - Only those media devices of the given kind
 */
export function getDevicesOfKind(deviceInfos, kind) {
  return deviceInfos.filter((deviceInfo) => deviceInfo.kind === kind);
}

/**
 * Replace the existing LocalAudioTrack or LocalVideoTrack with
 * a new one in the Room.
 * @param {Room} room - The Room you have joined
 * @param {LocalAudioTrack|LocalVideoTrack} track - The LocalTrack you want to switch to
 * @param {Object} options - The Track's priority can be "low", "standard", or "high".
 * @returns {void}
 */
export function switchLocalTracks(room, track, options) {
  room.localParticipant.tracks.forEach((trackPublication) => {
    if (trackPublication.kind === track.kind) {
      trackPublication.track.stop();
      room.localParticipant.unpublishTrack(trackPublication.track);
    }
  });
  return room.localParticipant.publishTrack(track, options);
}

/**
 * Apply the selected audio output device.
 * @param {string} deviceId
 * @param {HTMLAudioElement} audio
 * @returns {Promise<void>}
 */
export function applyAudioOutputDeviceSelection(deviceId, audio) {
  debug('applyAudioOutputDeviceSelection');
  return typeof audio.setSinkId === 'function'
    ? audio.setSinkId(deviceId)
    : console.error(
        'This browser does not support setting an audio output device',
      );
}

/**
 * Apply the selected audio input device.
 * @param {string} deviceId
 * @param {HTMLAudioElement} audio
 * @param {Room} [room] - The Room, if you have already joined one
 * @returns {Promise<void>}
 */
export function applyAudioInputDeviceSelection(deviceId, audio, room) {
  debug('applyAudioInputDeviceSelection');
  return Video.createLocalAudioTrack({
    deviceId: {
      exact: deviceId, // NOTE: on ios safari - it respects the deviceId only if its exact.
    },
  })
    .then((localTrack) => {
      // localTrack.attach(audio);
      if (room) {
        switchLocalTracks(room, localTrack);
      }
      return localTrack;
    })
    .catch((error) => {
      console.error('applyAudioInputDeviceSelection failed:', error);
      silentError(error);
    });
}

/**
 * Apply the selected video input device.
 * @param {string} deviceId
 * @param {HTMLVideoElement} video
 * @param {Room} [room] - The Room, if you have already joined one
 * @returns {Promise<void>}
 */
export function applyVideoInputDeviceSelection(deviceId, video, room) {
  debug('applyVideoInputDeviceSelection');
  return Video.createLocalVideoTrack({
    deviceId: {
      exact: deviceId,
    },
    height: 720,
    frameRate: 24,
    width: 1280,
  })
    .catch((error) => {
      console.error(
        'applyVideoInputDeviceSelection createLocalVideoTrack failed:',
        error,
      );
      silentError(error);
    })
    .then((localTrack) => {
      // localTrack.attach(video);
      if (room) {
        switchLocalTracks(room, localTrack);
      }
      return localTrack;
    })
    .catch((error) => {
      console.error(
        'applyVideoInputDeviceSelection switchLocalTracks failed:',
        error,
      );
      silentError(error);
    });
}

/**
 * Ensure that media permissions are obtained.
 * @returns {Promise<void>}
 */
export function ensureMediaPermissions() {
  debug('ensureMediaPermissions');
  return navigator.mediaDevices
    .enumerateDevices()
    .then((devices) => {
      debug('ensureMediaPermissions enumerateDevices', devices);
      return devices.every((device) => !(device.id && device.label));
    })
    .then((shouldAskForMediaPermissions) => {
      debug(
        'ensureMediaPermissions shouldAskForMediaPermissions',
        shouldAskForMediaPermissions,
      );
      if (shouldAskForMediaPermissions) {
        // eslint-disable-next-line promise/no-nesting
        return (
          // eslint-disable-next-line promise/no-nesting
          navigator.mediaDevices
            .getUserMedia({ audio: true, video: true })
            .then((mediaStream) => {
              debug('ensureMediaPermissions trackStop');
              mediaStream.getTracks().forEach((track) => {
                track.stop();
              });
              debug('ensureMediaPermissions trackStop finished');
            })
            // If error request without video : https://e180.atlassian.net/browse/PAX-77
            .catch(() => {
              debug('ensureMediaPermissions catch');
              // eslint-disable-next-line promise/no-nesting
              return navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then((mediaStream) => {
                  mediaStream.getTracks().forEach((track) => {
                    // Need to stop this media because it is use only for permission purpose
                    // https://e180.atlassian.net/browse/PAX-160
                    track.stop();
                  });
                });
            })
        );
      }
    });
}

/**
 * Get the list of available media devices.
 * @returns {Promise<DeviceSelectionOptions>}
 * @typedef {object} DeviceSelectionOptions
 * @property {Array<MediaDeviceInfo>} audioinput
 * @property {Array<MediaDeviceInfo>} audiooutput
 * @property {Array<MediaDeviceInfo>} videoinput
 */
export function getDeviceSelectionOptions() {
  debug('getDeviceSelectionOptions');
  // before calling enumerateDevices, get media permissions (.getUserMedia)
  // w/o media permissions, browsers do not return device Ids and/or labels.
  return ensureMediaPermissions().then(() => {
    debug('getDeviceSelectionOptions ensureMediaPermissions');
    // eslint-disable-next-line promise/no-nesting
    return navigator.mediaDevices.enumerateDevices().then((deviceInfos) => {
      debug(
        'getDeviceSelectionOptions ensureMediaPermissions enumerateDevices',
      );
      const kinds = ['audioinput', 'audiooutput', 'videoinput'];
      return kinds.reduce((deviceSelectionOptions, kind) => {
        deviceSelectionOptions[kind] = getDevicesOfKind(deviceInfos, kind);
        return deviceSelectionOptions;
      }, {});
    });
  });
}

/**
 * Connects to room using specified input devices
 * @param {string} token
 * @param {string} audioDeviceId
 * @param {string} videoDeviceId
 * @returns {Promise<Room>}
 */
export function connectWithSelectedDevices(
  token,
  audioDeviceId,
  videoDeviceId,
) {
  debug('connectWithSelectedDevices');
  return Video.connect(token, {
    audio: { deviceId: { exact: audioDeviceId } },
    video: { deviceId: { exact: videoDeviceId } },
    dominantSpeaker: true,
  });
}

let AudioContext = null;

if (typeof window !== 'undefined') {
  AudioContext = window
    ? window.AudioContext || window.webkitAudioContext
    : null;
}

const audioContext = AudioContext ? new AudioContext() : null;

/**
 * Calculate the root mean square (RMS) of the given array.
 * @param samples
 * @returns {number} the RMS value
 */
function rootMeanSquare(samples) {
  const sumSq = samples.reduce((sumSq, sample) => sumSq + sample * sample, 0);
  return Math.sqrt(sumSq / samples.length);
}

/**
 * Poll the microphone's input level.
 * @param stream - the MediaStream representing the microphone
 * @param maxLevel - the calculated level should be in the range [0 - maxLevel]
 * @param onLevel - called when the input level changes
 * @param cancelAnimationFrameRequestId - requestID to cancel before setting a new callback
 */
export const micLevel = audioContext
  ? function micLevel(stream, maxLevel, onLevel) {
      return audioContext
        .resume()
        .then(() => {
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 1024;
          analyser.smoothingTimeConstant = 0.9;

          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
          const samples = new Uint8Array(analyser.frequencyBinCount);

          const [track] = stream.getTracks();
          let level = null;

          function checkLevel() {
            analyser.getByteFrequencyData(samples);
            const rms = rootMeanSquare(samples);
            const log2Rms = rms && Math.log2(rms);
            const newLevel = Math.ceil((maxLevel * log2Rms) / 8);
            debug('micLevel');

            if (level !== newLevel) {
              level = newLevel;
              onLevel(level);
            }

            if (track.readyState === 'ended') {
              onLevel(0);
              clearInterval(interval);
            }
          }

          const interval = setInterval(checkLevel, 10);

          return interval;
        })
        .catch((e) => silentError(e));
    }
  : function notSupported() {
      // Do nothing.
    };

export function isProvider(event, provider) {
  if (!provider) {
    return false;
  }
  const plugins = getEventPlugins(event);
  return plugins[videoCallPlugin].provider === provider;
}

export function isSupported(event) {
  if (isProvider(event, videoCallPluginProviderTwilio)) {
    return Video.isSupported;
  }
  return true;
}

/**
 * Create a LocalVideoTrack for your screen. You can then share it
 * with other Participants in the Room.
 * @param {object} dimensions
 * @returns {Promise<LocalVideoTrack>}
 */
export function createScreenTrack(dimensions, room) {
  debug('createScreenTrack', dimensions, room);
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices ||
    !navigator.mediaDevices.getDisplayMedia
  ) {
    return Promise.reject(new Error('getDisplayMedia is not supported'));
  }
  return navigator.mediaDevices
    .getDisplayMedia({
      video: dimensions,
    })
    .then((stream) => stream.getVideoTracks()[0])
    .catch((error) => {
      console.error('createScreenTrack failed:', error);
      silentError(error);
    });
}

export function getScreenDimensions() {
  if (screen) {
    return {
      height: screen.height,
      width: screen.width,
    };
  }
  return { height: 1280, width: 720 };
}
